import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import * as mutations from '../../graphql/mutations';
import * as subscriptions from '../../graphql/subscriptions';
import useOperationalStore, {
  STATUS_TO_STAGE, ACTIVITY_TYPES, normalizeActivities,
  BLOCKER_LABELS, getActiveBlocker, fmtDur, getStageLabel,
} from '../store/useOperationalStore';
import AssignTechModal from '../../components/Modals/AssignTechModal';
import { EVENT_TYPES, recordTruckEvent, nowSeconds } from '../events/operationEvents';
import { getCurrentRole, hasPermission, PERMISSIONS } from '../../shared/permissions';
import { activeEstimate, moneyState } from '../engine/estimateMoney';
import { calcLineTotals, STATUS_CONFIG } from '../../views/EstimatePanel';
import { getLifecycleStops, getLifecycleStopIndex, getMoneyGateWarning, hasGateOverride } from '../../views/ro/roMoney';
import useShopSettings from '../settings/useShopSettings';
import './RODetailPanel.css';

const client = generateClient();

const NEXT_ACTION = {
  yard:         { label: 'Start Diagnosis', next: 'waiting' },
  triage:       { label: 'Move to Repair',  next: 'wip' },    // backward compat
  waiting:      { label: 'Move to Repair',  next: 'wip' },
  approval:     { label: 'Start Repair',    next: 'wip' },
  'parts-auth': { label: 'Start Repair',    next: 'wip' },
  parts:        { label: 'Start Repair',    next: 'wip' },
  'parts-hold': { label: 'Start Repair',    next: 'wip' },
  wip:          { label: 'Road Test',       next: 'road-test' },
  'road-test':  { label: 'Mark Complete',   next: 'done' },
  qc:           { label: 'Mark Complete',   next: 'done' },   // backward compat
};

const ACT_LABELS = {
  note: 'Note', finding: 'Finding', repair: 'Repair',
  authorization: 'Auth', customer_update: 'Customer',
  status_change: 'Status', photo: 'Photo',
  blocker_set: 'Blocked', blocker_cleared: 'Unblocked',
};

function clean(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(clean);
  const { __typename, ...rest } = obj;
  return Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, clean(v)]));
}

function makeActivityId() {
  return `act-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function tsToMs(ts) {
  if (!ts) return null;
  return ts < 10_000_000_000 ? ts * 1000 : ts;
}

function fmtRelative(ts) {
  if (!ts) return null;
  const diff = Date.now() - (tsToMs(ts) ?? 0);
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(tsToMs(ts)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getTechs(truck) {
  if (truck?.techNumbers?.length) return truck.techNumbers.filter(Boolean).map(String);
  return truck?.techNumber ? [String(truck.techNumber)] : [];
}

// 8-stop lifecycle strip (view-only) — same getLifecycleStopIndex the full
// job page's LifecycleRail uses, so the board panel and the job page agree
// on where in the money loop a job actually is.
function LifecycleDots({ truck, settings }) {
  const stops = getLifecycleStops(truck, settings);
  const curIdx = getLifecycleStopIndex(truck, settings);
  return (
    <div className="rdp-pipeline">
      {stops.map((stop, i) => (
        <React.Fragment key={stop.id}>
          <div
            className={`rdp-pipe-dot ${
              i < curIdx ? 'rdp-dot-past' : i === curIdx ? 'rdp-dot-current' : 'rdp-dot-future'
            }`}
            title={stop.label}
          />
          {i < stops.length - 1 && (
            <div className={`rdp-pipe-line${i < curIdx ? ' rdp-line-past' : ''}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function RODetailPanel() {
  const navigate = useNavigate();

  const truck              = useOperationalStore(s => s.trucks.find(t => t.id === s.selectedTruckId) ?? null);
  const setSelectedTruckId = useOperationalStore(s => s.setSelectedTruckId);
  const localUpdateTruck   = useOperationalStore(s => s.localUpdateTruck);
  const settings           = useShopSettings(s => s.settings);

  const [noteText,        setNoteText]        = useState('');
  const [submitting,      setSubmitting]      = useState(false);
  const [assignTechOpen,  setAssignTechOpen]  = useState(false);
  const [changingStage,   setChangingStage]   = useState(false);
  const [resolvingBlocker, setResolvingBlocker] = useState(false);
  const [now,             setNow]             = useState(() => Date.now());
  const subRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!truck?.id) return;
    const sub = client.graphql({
      query: subscriptions.onUpdateTruck,
      variables: { filter: { id: { eq: truck.id } } },
    }).subscribe({
      next: ({ data }) => {
        if (data?.onUpdateTruck) localUpdateTruck(truck.id, data.onUpdateTruck);
      },
    });
    subRef.current = sub;
    return () => sub.unsubscribe();
  }, [truck?.id]); // subscription re-attaches only when truck id changes

  if (!truck) return null;

  const isClosed    = ['completed', 'voided'].includes(truck.status);
  const blocker     = getActiveBlocker(truck);
  const activities  = normalizeActivities(truck);
  const lastAct     = activities[0] ?? null;
  const techs       = getTechs(truck);
  const stageLabel  = getStageLabel(truck.status);
  const lifecycleStops = getLifecycleStops(truck, settings);
  const lifecycleIdx = getLifecycleStopIndex(truck, settings);
  const lifecycleLabel = lifecycleStops[lifecycleIdx]?.label ?? stageLabel;
  const nextAction  = NEXT_ACTION[truck.status] ?? null;
  const currentRole = getCurrentRole();
  const canMove     = hasPermission(currentRole, PERMISSIONS.MOVE_WORKFLOW_STAGE);
  const canNote     = hasPermission(currentRole, PERMISSIONS.ADD_NOTES);
  const canEdit     = hasPermission(currentRole, PERMISSIONS.EDIT_WORK_ORDERS);
  const totalMs     = isClosed ? 0 : now - (tsToMs(truck.startTime) ?? now);

  function close() {
    setSelectedTruckId(null);
    setNoteText('');
  }

  async function handleAddNote() {
    if (!canNote) return;
    const text = noteText.trim();
    if (!text) return;
    setSubmitting(true);
    const ts = nowSeconds();
    const entry = {
      id: makeActivityId(),
      type: ACTIVITY_TYPES.NOTE,
      text,
      author: currentRole,
      timestamp: ts,
      photos: [],
      customerFacing: false,
      category: null,
      stage: STATUS_TO_STAGE[truck.status] ?? null,
    };
    const updated = [entry, ...clean(truck.activities || [])];
    localUpdateTruck(truck.id, { activities: updated, lastNote: ts });
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: truck.id, activities: updated, lastNote: ts } },
      });
      recordTruckEvent(truck, EVENT_TYPES.NOTE_ADDED, { timestamp: ts, note: text });
      setNoteText('');
    } catch (e) { console.error(e); }
    setSubmitting(false);
  }

  async function handleNextStage() {
    if (!canMove || !nextAction) return;

    // Same soft money gate as the job page: skipping a money step asks once
    // and logs the override — never hard-blocks.
    const gate = getMoneyGateWarning(truck, nextAction.next, settings);
    let gateActivity = null;
    if (gate && !hasGateOverride(truck, gate.id)) {
      if (!window.confirm(gate.prompt)) return;
      const role = getCurrentRole();
      gateActivity = {
        id: makeActivityId(),
        type: ACTIVITY_TYPES.AUTHORIZATION,
        text: `${gate.overrideText} — by ${role}`,
        author: role,
        timestamp: nowSeconds(),
        photos: [],
        customerFacing: false,
        category: null,
        stage: STATUS_TO_STAGE[nextAction.next] ?? null,
      };
    }

    setChangingStage(true);
    const ts = nowSeconds();
    const history = (truck.statusHistory || [])
      .filter(e => e?.status && e?.timeEntered)
      .map(e => ({ status: e.status, timeEntered: e.timeEntered }));
    const newHistory = [...history, { status: nextAction.next, timeEntered: ts }];
    const patch = {
      status: nextAction.next, lastStatusChange: ts, statusHistory: newHistory,
      ...(gateActivity ? { activities: [gateActivity, ...clean(truck.activities || [])] } : {}),
    };
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: truck.id, ...patch } },
      });
      recordTruckEvent(truck, EVENT_TYPES.STATUS_CHANGED, {
        timestamp: ts, previousStatus: truck.status, newStatus: nextAction.next,
      });
    } catch (e) { console.error(e); }
    setChangingStage(false);
  }

  async function handleUpdateTechs(newTechs) {
    if (!canEdit) return;
    localUpdateTruck(truck.id, { techNumbers: newTechs });
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: truck.id, techNumbers: newTechs } },
      });
    } catch (e) { console.error(e); }
    setAssignTechOpen(false);
  }

  async function handleResolveBlocker() {
    if (!canNote || !blocker) return;
    setResolvingBlocker(true);
    const ts = nowSeconds();
    const resolved = { ...clean(blocker), resolvedAt: ts, resolvedBy: currentRole };
    const blockerHistory = [...clean(truck.blockerHistory || []), resolved];
    const entry = {
      id: makeActivityId(),
      type: ACTIVITY_TYPES.BLOCKER_CLEARED,
      text: `Resolved: ${BLOCKER_LABELS[blocker.type] || blocker.type}`,
      author: currentRole,
      timestamp: ts,
      photos: [],
      customerFacing: false,
      category: null,
      stage: STATUS_TO_STAGE[truck.status] ?? null,
    };
    const updatedActs = [entry, ...clean(truck.activities || [])];
    localUpdateTruck(truck.id, { currentBlocker: null, blockerHistory, activities: updatedActs });
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: truck.id, currentBlocker: null, blockerHistory, activities: updatedActs } },
      });
    } catch (e) { console.error(e); }
    setResolvingBlocker(false);
  }

  const vehicleDesc = [truck.year, truck.make, truck.model].filter(Boolean).join(' ');

  return (
    <>
      <div className="rdp-backdrop" onClick={close} />

      <div className={`rdp-panel${isClosed ? ' rdp-panel--closed' : ''}`}>

        {/* Header */}
        <div className="rdp-header">
          {/* Row 1: RO # + status badge + warning badges + age */}
          <div className="rdp-header-row1">
            <span className="rdp-ro">{truck.repairOrder || '—'}</span>
            <span className="rdp-status-badge">{stageLabel}</span>
            <div className="rdp-header-badges">
              {truck.warranty   && <span className="rdp-badge rdp-badge--w">W</span>}
              {truck.isHardDown && <span className="rdp-badge rdp-badge--fd">FD</span>}
            </div>
            <span className="rdp-age">{fmtDur(totalMs)}</span>
          </div>

          {/* Row 2: Company + contact icons */}
          <div className="rdp-customer-row">
            <div>
              <div className="rdp-company">{truck.companyName || '—'}</div>
              {truck.contactPhone && (
                <div className="rdp-phone">{truck.contactPhone}</div>
              )}
            </div>
            <div className="rdp-contact-icons">
              {truck.contactPhone ? (
                <a
                  className="rdp-contact-btn"
                  href={`tel:${truck.contactPhone}`}
                  title="Call"
                  style={{ textDecoration: 'none' }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 .5h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.37a16 16 0 006.54 6.54l1.23-1.23a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                </a>
              ) : (
                <button className="rdp-contact-btn" disabled title="No phone on file" style={{ opacity: 0.4, cursor: 'default' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 .5h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.37a16 16 0 006.54 6.54l1.23-1.23a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                </button>
              )}
              {truck.contactEmail ? (
                <a
                  className="rdp-contact-btn"
                  href={`mailto:${truck.contactEmail}`}
                  title="Email"
                  style={{ textDecoration: 'none' }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </a>
              ) : (
                <button className="rdp-contact-btn" disabled title="No email on file" style={{ opacity: 0.4, cursor: 'default' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Row 3: Unit # + vehicle */}
          {(truck.truckNumber || vehicleDesc) && (
            <div className="rdp-vehicle-row">
              {truck.truckNumber && <span className="rdp-vehicle-name">#{truck.truckNumber}</span>}
              {vehicleDesc && <span className="rdp-vehicle-vin">{vehicleDesc}</span>}
            </div>
          )}

          <button className="rdp-close" onClick={close} title="Close">✕</button>
        </div>

        {/* Pipeline strip */}
        <div className="rdp-pipeline-wrap">
          <LifecycleDots truck={truck} settings={settings} />
          <span className="rdp-stage-label">{lifecycleLabel}</span>
        </div>

        {/* Blocker bar */}
        {blocker && (
          <div className="rdp-blocker-bar">
            <span className="rdp-blocker-icon">⚠</span>
            <span className="rdp-blocker-text">
              <strong>{BLOCKER_LABELS[blocker.type] || 'Blocked'}</strong>
              {blocker.description && ` — ${blocker.description}`}
            </span>
            {canNote && (
              <button
                className="rdp-blocker-resolve"
                onClick={handleResolveBlocker}
                disabled={resolvingBlocker}
              >
                {resolvingBlocker ? '…' : 'Resolve'}
              </button>
            )}
          </div>
        )}

        {/* Complaint */}
        <div className="rdp-complaint-wrap">
          <span className="rdp-complaint-label">Complaint</span>
          <p className="rdp-complaint">
            {truck.complaint || truck.initialComplaint || <em>No complaint recorded</em>}
          </p>
        </div>

        {/* Scrollable body */}
        <div className="rdp-body">

          {/* Money — mirrors the job page's money-rail card order:
              Estimate, Labor, Parts, Invoice, Comms. Read-only here;
              editing happens on the full RO page. */}
          {(() => {
            const est = activeEstimate(truck);
            const { stage, remaining } = moneyState(truck);
            const sc = STATUS_CONFIG[stage];
            const laborHours = est
              ? (est.lines || []).reduce((s, ln) => s + calcLineTotals(ln, truck.parts).laborHours, 0)
              : 0;
            const partsTotal = (truck.parts || []).reduce(
              (s, p) => s + (parseFloat(p.price) || 0) * (parseFloat(p.quantity) || 1), 0);
            const commsCount = (truck.activities || [])
              .filter(a => a.customerFacing || a.type === 'authorization').length;
            const invoiceLabel =
              stage === 'paid'     ? 'Paid' :
              stage === 'invoiced' ? `$${remaining.toFixed(0)} due` :
              (truck.status === 'done' && !['invoiced', 'paid'].includes(stage)) ? 'Ready to invoice' :
              '—';

            return (
              <div className="rdp-section">
                <div className="rdp-section-head">
                  <span className="rdp-section-title">Money</span>
                </div>
                <div className="rdp-money-rows">
                  <div className="rdp-money-row">
                    <span>Estimate</span>
                    <span className="rdp-money-chip" style={est && sc ? { color: sc.color, background: sc.bg } : undefined}>
                      {est ? sc.label : 'None'}
                    </span>
                  </div>
                  <div className="rdp-money-row">
                    <span>Labor</span>
                    <span>{laborHours > 0 ? `${laborHours.toFixed(1)}h` : '—'}</span>
                  </div>
                  <div className="rdp-money-row">
                    <span>Parts</span>
                    <span>{truck.parts?.length ? `${truck.parts.length} · $${partsTotal.toFixed(0)}` : '—'}</span>
                  </div>
                  <div className="rdp-money-row">
                    <span>Invoice</span>
                    <span>{invoiceLabel}</span>
                  </div>
                  <div className="rdp-money-row">
                    <span>Comms</span>
                    <span>{commsCount > 0 ? `${commsCount} logged` : '—'}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Assigned tech */}
          <div className="rdp-section">
            <div className="rdp-section-head">
              <span className="rdp-section-title">Technicians</span>
              {!isClosed && canEdit && (
                <button className="rdp-action-link" onClick={() => setAssignTechOpen(true)}>
                  {techs.length ? 'Change' : '+ Assign'}
                </button>
              )}
            </div>
            <div className="rdp-techs">
              {techs.length
                ? techs.map(n => <span key={n} className="rdp-tech-badge">{n}</span>)
                : <span className="rdp-muted">Unassigned</span>
              }
            </div>
          </div>

          {/* Last activity */}
          {lastAct && (
            <div className="rdp-section">
              <div className="rdp-section-head">
                <span className="rdp-section-title">Last Activity</span>
                <span className="rdp-muted" style={{ fontSize: 11 }}>{fmtRelative(lastAct.timestamp)}</span>
              </div>
              <div className="rdp-last-act">
                <div className="rdp-last-act-meta">
                  <span className={`rdp-act-badge rdp-act-badge--${lastAct.type}`}>
                    {ACT_LABELS[lastAct.type] || lastAct.type}
                  </span>
                  {lastAct.author && <span className="rdp-last-act-author">{lastAct.author}</span>}
                </div>
                {lastAct.text && <p className="rdp-last-act-text">{lastAct.text}</p>}
              </div>
            </div>
          )}

        </div>

        {/* Action row */}
        <div className="rdp-footer">
          {!isClosed && (
            <div className="rdp-note-input-wrap">
              <textarea
                className="rdp-note-input"
                placeholder="Quick note…"
                rows={1}
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); }
                }}
              />
              <button
                className="rdp-note-submit"
                onClick={handleAddNote}
                disabled={!noteText.trim() || submitting}
              >
                {submitting ? '…' : 'Send'}
              </button>
            </div>
          )}
          <div className="rdp-action-btns">
            {!isClosed && nextAction && canMove && (
              <button
                className="rdp-btn rdp-btn--next"
                onClick={handleNextStage}
                disabled={changingStage}
              >
                {changingStage ? '…' : nextAction.label}
              </button>
            )}
            <button
              className="rdp-btn rdp-btn--open-ro"
              onClick={() => { navigate(`/ro/${truck.id}`); close(); }}
            >
              Open Full RO →
            </button>
          </div>
        </div>

      </div>

      {assignTechOpen && (
        <AssignTechModal
          onClose={() => setAssignTechOpen(false)}
          onSubmit={handleUpdateTechs}
          currentTechs={techs}
        />
      )}
    </>
  );
}
